import { useEffect, useMemo, useState } from 'react'

import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    InputAdornment,
    LinearProgress,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import axios from 'axios'

import Icon from '../../../components/icon'
import MsiDatagrid from '../../../components/custom/MsiDatagridAuto'
import { useToast } from '../../../components/ToastProvider'
import apiUrl from '../../../configs/constant'

// Sesuaikan hanya path import ini dengan helper getPeriode yang sudah ada.
import { getPeriode } from '../../../api/helper'

interface PeriodeOption {
    id: string | number
    periode: string
    [key: string]: unknown
}

type ReportRow = Record<string, unknown>

const noActions: any[] = []

const readToken = (): string => {
    try {
        const rawUser = localStorage.getItem('user')

        if (!rawUser) return ''

        return String(JSON.parse(rawUser)?.token ?? '')
    } catch {
        return ''
    }
}

const getErrorMessage = (error: unknown, fallback: string): string => {
    if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message

        if (typeof message === 'string' && message.trim()) {
            return message.replace(/<br\s*\/?>/gi, '\n')
        }

        return error.message || fallback
    }

    if (error instanceof Error) return error.message

    return fallback
}

const normalizePeriodeResponse = (response: any): PeriodeOption[] => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.data)) return response.data
    if (Array.isArray(response?.data?.data)) return response.data.data

    return []
}

const MetricCard = ({
    icon,
    label,
    value,
    helper
}: {
    icon: string
    label: string
    value: string | number
    helper: string
}) => (
    <Paper
        variant='outlined'
        sx={{
            height: '100%',
            p: 2.25,
            borderRadius: 3,
            borderColor: 'divider',
            backgroundColor: 'background.paper'
        }}
    >
        <Stack direction='row' spacing={1.75} alignItems='center'>
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: 2.25,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'primary.main',
                    backgroundColor: 'action.hover'
                }}
            >
                <Icon icon={icon} fontSize={24} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
                <Typography variant='caption' color='text.secondary'>
                    {label}
                </Typography>
                <Typography
                    variant='h6'
                    sx={{
                        lineHeight: 1.25,
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {value}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                    {helper}
                </Typography>
            </Box>
        </Stack>
    </Paper>
)

const EmptyState = ({
    hasSearched
}: {
    hasSearched: boolean
}) => (
    <Box
        sx={{
            minHeight: 300,
            px: 3,
            py: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
        }}
    >
        <Box sx={{ maxWidth: 440 }}>
            <Box
                sx={{
                    width: 72,
                    height: 72,
                    mx: 'auto',
                    mb: 2,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'primary.main',
                    backgroundColor: 'action.hover'
                }}
            >
                <Icon
                    icon={hasSearched ? 'mdi:database-off-outline' : 'mdi:chart-box-outline'}
                    fontSize={34}
                />
            </Box>

            <Typography variant='h6' sx={{ mb: 0.75, fontWeight: 700 }}>
                {hasSearched ? 'Data tidak ditemukan' : 'Pilih periode laporan'}
            </Typography>

            <Typography variant='body2' color='text.secondary'>
                {hasSearched
                    ? 'Stored procedure tidak mengembalikan data pada periode yang dipilih.'
                    : 'Pilih periode terlebih dahulu, kemudian tekan tombol Tampilkan Data.'}
            </Typography>
        </Box>
    </Box>
)

export default function Index() {
    const token = readToken()
    const { showToast } = useToast()

    const [listPeriode, setListPeriode] = useState<PeriodeOption[]>([])
    const [valPeriode, setValPeriode] = useState<PeriodeOption | null>(null)
    const [listData, setListData] = useState<ReportRow[]>([])
    const [keyword, setKeyword] = useState<string>('')
    const [loadingPeriode, setLoadingPeriode] = useState<boolean>(false)
    const [loadingData, setLoadingData] = useState<boolean>(false)
    const [hasSearched, setHasSearched] = useState<boolean>(false)
    const [lastUpdated, setLastUpdated] = useState<string>('-')

    useEffect(() => {
        void fetchPeriode()
    }, [])

    const fetchPeriode = async (): Promise<void> => {
        setLoadingPeriode(true)

        try {
            const response = await getPeriode(token)
            const periode = normalizePeriodeResponse(response)

            setListPeriode(periode)
        } catch (error) {
            showToast(
                getErrorMessage(error, 'Gagal mengambil daftar periode.'),
                'error'
            )
        } finally {
            setLoadingPeriode(false)
        }
    }

    const getData = async (): Promise<void> => {
        if (!valPeriode?.id) {
            showToast('Periode harus dipilih terlebih dahulu.', 'error')

            return
        }

        setLoadingData(true)
        setKeyword('')

        try {
            const response = await axios.get(
                `${apiUrl.apiUrl}/report/valuasi-stock/list`,
                {
                    params: {
                        id_periode: String(valPeriode.id)
                    },
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            if (!response.data?.success) {
                showToast(
                    response.data?.message || 'Data valuasi stock gagal diambil.',
                    'error'
                )

                return
            }

            const rows = Array.isArray(response.data?.data)
                ? response.data.data.map((item, index) => ({
                    ...item,
                    id: index + 1,
                }))
                : []

            setListData(rows)
            setHasSearched(true)
            setLastUpdated(new Date().toLocaleString('id-ID'))

            showToast(response.data?.message || 'Data berhasil diambil.', 'success')
        } catch (error) {
            setListData([])
            setHasSearched(true)

            showToast(
                getErrorMessage(error, 'Gagal mengambil data valuasi stock.'),
                'error'
            )
        } finally {
            setLoadingData(false)
        }
    }

    const resetFilter = (): void => {
        setValPeriode(null)
        setListData([])
        setKeyword('')
        setHasSearched(false)
        setLastUpdated('-')
    }

    const filteredData = useMemo(() => {
        const search = keyword.trim().toLowerCase()

        if (!search) return listData

        return listData.filter(row =>
            Object.values(row).some(value =>
                String(value ?? '')
                    .toLowerCase()
                    .includes(search)
            )
        )
    }, [keyword, listData])

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Card
                    sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: theme => `1px solid ${theme.palette.divider}`,
                        boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)'
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2.5, md: 4 },
                            py: { xs: 3, md: 3.75 },
                            color: 'common.white',
                            background:
                                'linear-gradient(135deg, #0f172a 0%, #1e3a8a 58%, #2563eb 100%)'
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2.25}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent='space-between'
                        >
                            <Stack direction='row' spacing={2} alignItems='center'>
                                <Box
                                    sx={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: 3,
                                        display: 'grid',
                                        placeItems: 'center',
                                        backgroundColor: 'rgba(255,255,255,0.14)',
                                        border: '1px solid rgba(255,255,255,0.2)'
                                    }}
                                >
                                    <Icon icon='mdi:chart-areaspline-variant' fontSize={30} />
                                </Box>

                                <Box>
                                    <Typography variant='h4' sx={{ fontWeight: 800 }}>
                                        Valuasi Stock
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        sx={{ mt: 0.5, color: 'rgba(255,255,255,0.78)' }}
                                    >
                                        Laporan nilai persediaan berdasarkan periode pembukuan.
                                    </Typography>
                                </Box>
                            </Stack>

                            <Chip
                                icon={<Icon icon='mdi:database-check-outline' fontSize={18} />}
                                label='Laporan Persediaan'
                                sx={{
                                    color: 'common.white',
                                    backgroundColor: 'rgba(255,255,255,0.14)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    '& .MuiChip-icon': { color: 'inherit' }
                                }}
                            />
                        </Stack>
                    </Box>

                    {loadingData && <LinearProgress />}

                    <CardContent sx={{ p: { xs: 2, md: 3.5 } }}>
                        <Paper
                            variant='outlined'
                            sx={{
                                p: { xs: 2, md: 2.75 },
                                mb: 3,
                                borderRadius: 3,
                                backgroundColor: 'background.default'
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={2}
                                alignItems={{ xs: 'stretch', md: 'center' }}
                            >
                                <Box sx={{ minWidth: { md: 220 } }}>
                                    <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                                        Filter Laporan
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary'>
                                        Tentukan periode valuasi yang akan ditampilkan.
                                    </Typography>
                                </Box>

                                <Autocomplete<PeriodeOption>
                                    fullWidth
                                    value={valPeriode}
                                    options={listPeriode}
                                    loading={loadingPeriode}
                                    disabled={loadingData}
                                    isOptionEqualToValue={(option, value) =>
                                        String(option.id) === String(value?.id)
                                    }
                                    getOptionLabel={option => option?.periode || ''}
                                    onChange={(_, newValue) => {
                                        setValPeriode(newValue)
                                        setListData([])
                                        setHasSearched(false)
                                        setKeyword('')
                                        setLastUpdated('-')
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label='Periode'
                                            placeholder='Pilih periode'
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position='start'>
                                                            <Icon icon='mdi:calendar-month-outline' />
                                                        </InputAdornment>
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                                endAdornment: (
                                                    <>
                                                        {loadingPeriode ? (
                                                            <CircularProgress color='inherit' size={20} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />

                                <Button
                                    variant='contained'
                                    size='large'
                                    disabled={!valPeriode || loadingData}
                                    startIcon={
                                        loadingData ? (
                                            <CircularProgress size={18} color='inherit' />
                                        ) : (
                                            <Icon icon='mdi:magnify' />
                                        )
                                    }
                                    onClick={() => void getData()}
                                    sx={{
                                        minWidth: { md: 180 },
                                        minHeight: 52,
                                        borderRadius: 2.25,
                                        textTransform: 'none',
                                        fontWeight: 700
                                    }}
                                >
                                    {loadingData ? 'Memuat...' : 'Tampilkan Data'}
                                </Button>

                                <Button
                                    variant='outlined'
                                    size='large'
                                    disabled={loadingData}
                                    startIcon={<Icon icon='mdi:refresh' />}
                                    onClick={resetFilter}
                                    sx={{
                                        minHeight: 52,
                                        borderRadius: 2.25,
                                        textTransform: 'none',
                                        fontWeight: 700
                                    }}
                                >
                                    Reset
                                </Button>
                            </Stack>
                        </Paper>

                        {hasSearched && (
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={4}>
                                    <MetricCard
                                        icon='mdi:table-large'
                                        label='Jumlah Data'
                                        value={listData.length.toLocaleString('id-ID')}
                                        helper='Baris hasil laporan'
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <MetricCard
                                        icon='mdi:calendar-check-outline'
                                        label='Periode Terpilih'
                                        value={valPeriode?.periode || '-'}
                                        helper={`ID: ${valPeriode?.id ?? '-'}`}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <MetricCard
                                        icon='mdi:clock-check-outline'
                                        label='Terakhir Diperbarui'
                                        value={lastUpdated}
                                        helper='Waktu pengambilan data'
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <Paper
                            variant='outlined'
                            sx={{
                                overflow: 'hidden',
                                borderRadius: 3,
                                borderColor: 'divider'
                            }}
                        >
                            <Box
                                sx={{
                                    px: { xs: 2, md: 2.5 },
                                    py: 2,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    justifyContent: 'space-between',
                                    gap: 1.5
                                }}
                            >
                                <Box>
                                    <Typography variant='h6' sx={{ fontWeight: 700 }}>
                                        Hasil Valuasi Stock
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary'>
                                        Kolom tabel mengikuti hasil stored procedure.
                                    </Typography>
                                </Box>

                                {listData.length > 0 && (
                                    <Stack direction='row' spacing={1.25} alignItems='center'>
                                        <TextField
                                            size='small'
                                            value={keyword}
                                            onChange={event => setKeyword(event.target.value)}
                                            placeholder='Cari dalam hasil...'
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position='start'>
                                                        <Icon icon='mdi:magnify' />
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{ minWidth: { sm: 260 } }}
                                        />

                                        <Chip
                                            size='small'
                                            label={`${filteredData.length} baris`}
                                            variant='outlined'
                                        />
                                    </Stack>
                                )}
                            </Box>

                            <Divider />

                            {loadingData ? (
                                <Box
                                    sx={{
                                        minHeight: 300,
                                        display: 'grid',
                                        placeItems: 'center'
                                    }}
                                >
                                    <Stack alignItems='center' spacing={1.5}>
                                        <CircularProgress size={36} />
                                        <Typography variant='body2' color='text.secondary'>
                                            Mengambil data valuasi stock...
                                        </Typography>
                                    </Stack>
                                </Box>
                            ) : !hasSearched || listData.length === 0 ? (
                                <EmptyState hasSearched={hasSearched} />
                            ) : (
                                <Box sx={{ p: { xs: 1, md: 2 }, overflowX: 'auto' }}>
                                    <MsiDatagrid array={filteredData} actions={noActions} hiddenColumns={['id']} />
                                </Box>
                            )}
                        </Paper>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    )
}
